import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { LucideAngularModule, Lightbulb, FilePlus2, FileText, X, ChevronDown } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { THEME_COLOR } from '../../services/lesson-plan.models';
import { StepLoadingComponent } from '../step-loading/step-loading';

@Component({
  selector: 'app-step-topic',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, StepLoadingComponent],
  templateUrl: './step-topic.html',
  styleUrl: './step-topic.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepTopicComponent {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  @Output() readonly back = new EventEmitter<void>();
  @Output() readonly next = new EventEmitter<void>();
  @ViewChild(StepLoadingComponent) loadingComponent?: StepLoadingComponent;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  protected readonly themeColor = THEME_COLOR;
  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly uploadedFiles$ = this.lessonPlanService.uploadedFiles$;
  protected readonly error$ = this.lessonPlanService.error$;

  protected readonly acceptedFileTypes = '.pdf,.doc,.docx,.txt,.md';

  /** Whether we're showing the loading screen for generating objectives */
  private readonly showLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly showLoading$ = this.showLoading$$.asObservable();

  protected readonly icons = {
    Lightbulb,
    FilePlus2,
    FileText,
    X,
    ChevronDown,
  };

  protected updateTopic(value: string): void {
    this.lessonPlanService.updateInputData({ topic: value });
  }

  protected onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.lessonPlanService.addFiles(files);
      // Reset input so same file can be selected again
      input.value = '';
    }
  }

  protected removeFile(file: File): void {
    this.lessonPlanService.removeFile(file);
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  protected triggerFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

  protected onBack(): void {
    this.back.emit();
  }

  protected onGenerateObjectives(): void {
    // Show loading screen
    this.showLoading$$.next(true);

    // Update topic and generate objectives
    this.lessonPlanService
      .updateTopicAndGenerateObjectives()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Complete the loading animation
          this.loadingComponent?.complete();

          // Small delay to show 100% progress before transitioning
          setTimeout(() => {
            // Advance to objectives step (step 4)
            this.lessonPlanService.nextStep();
          }, 500);
        },
        error: () => {
          // Error is already handled by the service
          this.showLoading$$.next(false);
        },
      });
  }
}
